'use client';

import { useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '@/store/app';
import { useRequireAuth } from '@/lib/useRequireAuth';
import EventPing from '@/components/EventPing';
import { api } from '@/lib/api';
import { formatQuantity, formatPrice } from '@/lib/format';
import ResearchTabs from '@/components/ResearchTabs';

interface EventItem {
  eventKey: string;
  type: 'unlock' | 'economic';
  title: string;
  date: string;
  /** D07: 'dateOnly' when the source only carries a day (unlock calendar) — never show 08:00. */
  dateType?: 'dateOnly' | 'datetime';
  status: string;
  // unlock
  symbol?: string;
  symbols?: string[];
  tokens?: string;
  pctSupply?: number | null;
  price?: number | null;
  valuation?: number | null;
  description?: string | null;
  // economic
  country?: string;
  importance?: string;
  actual?: string | null;
  forecast?: string | null;
  previous?: string | null;
  source?: string;
  // D17: reminder-side meta — time revision visibility
  reminderId?: number;
  reminderStatus?: string;
  channel?: string;
  leadHours?: number;
  timeRevised?: boolean;
  prevDate?: string | null;
  revisedAt?: string | null;
}

interface EventsResponse {
  groups: Array<{ date: string; isToday?: boolean; events: EventItem[] }>;
  summary: { unlock: number; economic: number; days: number; asOf: string; watchlist?: string[] };
}

interface Reminder {
  id: number;
  eventKey: string;
  eventType: string;
  title: string;
  symbol: string | null;
  leadHours: number;
  status: string;
  eventDate: string;
}

const COUNTRY_FLAG: Record<string, string> = {
  USD: '🇺🇸', EUR: '🇪🇺', GBP: '🇬🇧', JPY: '🇯🇵', CNY: '🇨🇳', AUD: '🇦🇺',
  CAD: '🇨🇦', CHF: '🇨🇭', NZD: '🇳🇿', HKD: '🇭🇰', SGD: '🇸🇬', KRW: '🇰🇷',
  INR: '🇮🇳', BRL: '🇧🇷', MXN: '🇲🇽', RUB: '🇷🇺', TRY: '🇹🇷', ZAR: '🇿🇦',
  SEK: '🇸🇪', NOK: '🇳🇴', DKK: '🇩🇰', PLN: '🇵🇱', IDR: '🇮🇩', THB: '🇹🇭',
};

const IMPORTANCE_STYLE: Record<string, { badge: string; dot: string }> = {
  High: { badge: 'bg-gf-negative/15 text-gf-negative border-gf-negative/30', dot: 'bg-gf-negative' },
  Medium: { badge: 'bg-gf-warn/15 text-gf-warn border-gf-warn/30', dot: 'bg-gf-warn' },
  Low: { badge: 'bg-gf-surface2 text-gf-muted border-gf-border', dot: 'bg-gf-muted' },
};

const COUNTRY_ZH: Record<string, string> = {
  'Euro Zone': '欧元区', German: '德国', French: '法国', Italian: '意大利',
  Spanish: '西班牙', Japanese: '日本', Chinese: '中国', Australian: '澳洲',
  Canadian: '加拿大', Swiss: '瑞士', UK: '英国', Dutch: '荷兰', Belgian: '比利时',
  Austrian: '奥地利', 'New Zealand': '新西兰', Korean: '韩国', Brazilian: '巴西',
  Mexican: '墨西哥', Indian: '印度', Russian: '俄罗斯', Turkish: '土耳其',
  Polish: '波兰', Swedish: '瑞典', Norwegian: '挪威', Danish: '丹麦',
  Singaporean: '新加坡', 'South African': '南非', US: '美国', BoJ: '日本央行',
  BoE: '英国央行', ECB: '欧洲央行', RBA: '澳洲央行', BoC: '加拿大央行',
  SNB: '瑞士央行', RBNZ: '新西兰央行',
};

const EVENT_ZH_PATTERNS: [RegExp, string][] = [
  [/Inflation Rate/i, '通胀率'],
  [/GDP Growth Rate QoQ/i, 'GDP 增长率 季率'],
  [/GDP Growth Rate YoY/i, 'GDP 增长率 年率'],
  [/GDP Growth Rate/i, 'GDP 增长率'],
  [/Continuing Jobless Claims/i, '续请失业金'],
  [/Passenger Vehicles Sales/i, '乘用车销售'],
  [/Foreign Exchange Reserves/i, '外汇储备'],
  [/FOMC Statement/i, 'FOMC 声明'],
  [/FOMC Press Conference/i, 'FOMC 新闻发布会'],
  [/FOMC Member/i, 'FOMC 委员讲话'],
  [/FOMC/i, 'FOMC'],
  [/Meeting Minutes/i, '会议纪要'],
  [/Interest Rate Decision/i, '利率决议'],
  [/Federal Funds Rate/i, '联邦基金利率'],
  [/Core PCE Price Index/i, '核心 PCE 物价指数'],
  [/PCE Price Index/i, 'PCE 物价指数'],
  [/Core CPI/i, '核心 CPI'],
  [/CPI/i, 'CPI'],
  [/PPI/i, 'PPI'],
  [/Non-Farm Employment Change/i, '非农就业变动'],
  [/Nonfarm Payrolls/i, '非农就业'],
  [/Non-Farm Payrolls/i, '非农就业'],
  [/ADP Nonfarm Employment Change/i, 'ADP 非农就业变动'],
  [/ADP Employment Change/i, 'ADP 就业变动'],
  [/ADP/i, 'ADP'],
  [/Unemployment Rate/i, '失业率'],
  [/Unemployment Change/i, '失业变动'],
  [/Average Hourly Earnings/i, '平均时薪'],
  [/Initial Jobless Claims/i, '初请失业金'],
  [/Jobless Claims/i, '失业救济申请'],
  [/ISM Manufacturing/i, 'ISM 制造业'],
  [/ISM Services/i, 'ISM 服务业'],
  [/Retail Sales/i, '零售销售'],
  [/Trade Balance/i, '贸易收支'],
  [/Industrial Production/i, '工业生产'],
  [/Capacity Utilization/i, '产能利用率'],
  [/Consumer Confidence/i, '消费者信心'],
  [/Michigan Sentiment/i, '密歇根消费者信心'],
  [/Durable Goods Orders/i, '耐用品订单'],
  [/Housing Starts/i, '新屋开工'],
  [/Building Permits/i, '营建许可'],
  [/Existing Home Sales/i, '成屋销售'],
  [/New Home Sales/i, '新屋销售'],
  [/Current Account/i, '经常帐'],
  [/Crude Oil Inventories/i, '原油库存'],
  [/Crude Oil/i, '原油'],
  [/Philadelphia Fed/i, '费城联储'],
  [/Empire State/i, '纽约联储'],
  [/Chicago PMI/i, '芝加哥 PMI'],
  [/Producer Price Index/i, '生产者物价指数(PPI)'],
  [/GDP Price Index/i, 'GDP 物价指数'],
  [/GDP/i, 'GDP'],
  [/PMI/i, 'PMI'],
  [/MPC/i, '货币政策委员会'],
  [/Personal Income/i, '个人收入'],
  [/Personal Spending/i, '个人支出'],
  [/Prelim/i, '初值'],
  [/Flash/i, '初值'],
  [/Advance/i, '初值'],
  [/Final/i, '终值'],
  [/Bank Holiday/i, '银行假日'],
  [/Holiday/i, '假日'],
  [/Budget/i, '预算'],
  [/Bank Lending/i, '银行贷款'],
  [/Westpac/i, '西太平洋银行'],
  [/NODX/i, '非石油出口'],
  [/Exports/i, '出口'],
  [/Imports/i, '进口'],
  [/WPI/i, '批发物价'],
  [/QoQ/i, '季率'],
  [/m\/m/i, '月率'],
  [/q\/q/i, '季率'],
  [/y\/y/i, '年率'],
  [/YoY/i, '年率'],
  [/MoM/i, '月率'],
  [/Speech/i, '讲话'],
  [/Speaks/i, '讲话'],
  [/Auction/i, '拍卖'],
  [/Bond/i, '债券'],
  [/Price Index/i, '物价指数'],
  [/Price/i, '物价'],
  [/Index/i, '指数'],
  [/Balance/i, '收支'],
  [/Sentiment/i, '信心'],
  [/Employment/i, '就业'],
  [/Change/i, '变动'],
  [/Adv/i, '初值'],
  [/SA\b/i, '季节调整'],
  [/NSA\b/i, '未季调'],
];

function translateEventTitle(title: string): string {
  let t = title.replace(/\u00a0/g, ' ');
  for (const [en, zh] of Object.entries(COUNTRY_ZH)) {
    if (t.includes(en)) {
      t = t.replace(en, zh);
      break;
    }
  }
  for (const [re, zh] of EVENT_ZH_PATTERNS) {
    t = t.replace(re, zh);
  }
  return t.replace(/\s+/g, ' ').trim();
}

export default function ResearchEventsPage() {
  const { language, authState } = useAppStore();
  const authReady = useRequireAuth('/research/events', true);
  const zh = language !== 'en';
  const router = useRouter();
  const qc = useQueryClient();

  const [scope, setScope] = useState<'all' | 'watchlist'>('all');
  const [type, setType] = useState<'all' | 'unlock' | 'economic'>('all');
  const [days, setDays] = useState(30);
  const [remindFor, setRemindFor] = useState<EventItem | null>(null);
  const [remindChannel, setRemindChannel] = useState<'inapp' | 'both'>('inapp');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const authed = authState === 'authenticated';

  // D17: timezone summary shown in the reminder dialog.
  const tzLabel = (() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
      const off = -new Date().getTimezoneOffset() / 60;
      const sign = off >= 0 ? '+' : '-';
      return `${tz} (UTC${sign}${Math.abs(off)})`;
    } catch {
      return 'UTC';
    }
  })();

  const eventsQuery = useQuery({
    queryKey: ['research-events', scope, type, days],
    queryFn: () => {
      const tz = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Shanghai');
      return api.get<EventsResponse>(
        scope === 'watchlist'
          ? `/market/calendar/reminder-events?type=${type}&days=${days}&tz=${tz}`
          : `/market/calendar/events?type=${type}&days=${days}&tz=${tz}`,
      );
    },
    staleTime: 30_000,
    refetchInterval: 5 * 60_000,
    enabled: scope === 'all' || authed,
  });

  const remindersQuery = useQuery({
    queryKey: ['event-reminders'],
    queryFn: () => api.get<Reminder[]>('/market/calendar/reminders'),
    enabled: authed,
    staleTime: 30_000,
  });

  // 右侧「我的提醒近期事件」= 已订阅提醒的事件（与主列表 scope 无关）
  const watchQuery = useQuery({
    queryKey: ['research-events-watch', days],
    queryFn: () => {
      const tz = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Shanghai');
      return api.get<EventsResponse>(`/market/calendar/reminder-events?type=unlock&days=${Math.max(days, 30)}&tz=${tz}`);
    },
    enabled: authed,
    staleTime: 60_000,
    refetchInterval: 10 * 60_000,
  });

  const watchEvents = useMemo(
    () =>
      (watchQuery.data?.groups || [])
        .flatMap((g) => g.events)
        .filter((e) => e.type === 'unlock')
        .slice(0, 5),
    [watchQuery.data],
  );

  const remindedKeys = useMemo(
    () => new Set((remindersQuery.data || []).filter((r) => r.status === 'active').map((r) => r.eventKey)),
    [remindersQuery.data],
  );

  const groups = eventsQuery.data?.groups || [];
  const summary = eventsQuery.data?.summary;
  const now = Date.now();

  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(zh ? 'zh-TW' : 'en-US', {
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
  };
  const fmtTime = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };
  const isToday = (dateStr: string) => {
    // D07: groups are keyed by the browser timezone (server tz param), so "today"
    // must be the local calendar day, not the UTC day.
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return dateStr === `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  };
  const isPast = (iso: string) => new Date(iso).getTime() < now - 3600_000;
  const dayDiff = (iso: string) => {
    const d = new Date(iso);
    return Math.ceil((d.getTime() - now) / 86400000);
  };

  const fmtTokens = (v: string | undefined) => {
    if (!v) return '—';
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';
    return formatQuantity(n);
  };

  const handleRemind = (ev: EventItem) => {
    if (!authed) {
      router.push(`/login?redirect=${encodeURIComponent('/research/events')}`);
      return;
    }
    setRemindFor(ev);
  };

  const submitReminder = async (leadHours: number) => {
    if (!remindFor || saving) return;
    setSaving(true);
    try {
      await api.post('/market/calendar/reminders', {
        eventKey: remindFor.eventKey,
        eventType: remindFor.type,
        title: remindFor.title,
        symbol: remindFor.symbol,
        eventDate: remindFor.date,
        leadHours,
        channel: remindChannel,
      });
      setRemindFor(null);
      setToast(zh ? '提醒已设定' : 'Reminder set');
      qc.invalidateQueries({ queryKey: ['event-reminders'] });
      qc.invalidateQueries({ queryKey: ['research-events-watch'] });
      if (scope === 'watchlist') qc.invalidateQueries({ queryKey: ['research-events'] });
    } catch {
      setToast(zh ? '设定失败，请稍后重试' : 'Failed, please retry');
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 3000);
    }
  };

  const t = {
    title: zh ? '事件与供应' : 'Events & Supply',
    subtitle: zh
      ? '加密解锁、宏观经济事件与自选提醒，统一按日期查看'
      : 'Token unlocks, macro events and watchlist reminders, grouped by date',
    all: zh ? '全部事件' : 'All Events',
    watchlist: zh ? '我的自选' : 'My Watchlist',
    typeAll: zh ? '全部' : 'All',
    typeUnlock: zh ? '解锁' : 'Unlocks',
    typeEcon: zh ? '经济' : 'Economic',
    day30: '30D',
    day90: '90D',
    unlock: zh ? '解锁' : 'Unlock',
    economic: zh ? '经济' : 'Economic',
    upcoming: zh ? '即将到来' : 'Upcoming',
    ended: zh ? '已结束' : 'Ended',
    today: zh ? '今天' : 'Today',
    quantity: zh ? '数量' : 'Amount',
    pctSupply: zh ? '占流通量' : '% of Supply',
    valuation: zh ? '估值' : 'Est. Value',
    remind: zh ? '提醒' : 'Remind',
    reminded: zh ? '已提醒' : 'Reminded',
    remindTitle: zh ? '设定事件提醒' : 'Set event reminder',
    remindSub: zh ? '提前多久提醒我' : 'Remind me how early',
    channelTitle: zh ? '提醒方式' : 'Notify via',
    channelInapp: zh ? '站内通知' : 'In-app',
    channelBoth: zh ? '站内 + 邮件' : 'In-app + Email',
    channelNote: zh
      ? '提醒将通过站内通知推送（右上角铃铛 🔔 查看）；选择邮件将同步发送至注册邮箱。'
      : 'Reminders are pushed to the in-app notification center (bell icon). Email syncs to your registered mailbox.',
    lead1h: zh ? '1 小时前' : '1 hour before',
    lead24h: zh ? '1 天前' : '1 day before',
    lead3d: zh ? '3 天前' : '3 days before',
    dayOnlyHint: zh
      ? '此事件时间待定，仅能按日提醒'
      : 'This event has no fixed time — day-level reminders only',
    tzSummary: zh
      ? '事件时间按你的时区显示'
      : 'Event time shown in your timezone',
    cancel: zh ? '取消' : 'Cancel',
    saving: zh ? '设定中...' : 'Saving...',
    empty: zh ? '暂无事件数据' : 'No events',
    needLogin: zh ? '登入后查看我的提醒事件' : 'Log in to view your reminders',
    watchSummary: zh ? '我的提醒事件' : 'My Reminders',
    watchEmpty: zh ? '尚未订阅任何提醒，点击事件卡片 🔔 订阅' : 'No reminders yet. Tap 🔔 on any event to subscribe',
    supplyNote: zh ? '供应说明' : 'Supply Notes',
    supplyNoteBody: zh
      ? '解锁估值 = 解锁数量 × 参考价格，参考价格为当前市场价，估值时间为数据抓取时刻。比例 = 解锁数量 ÷ 所引用流通量 × 100%。'
      : 'Est. value = unlock amount × reference price (current market price, valued at fetch time). Ratio = unlock amount ÷ referenced circulating supply × 100%.',
    login: zh ? '登入' : 'Log in',
    source: zh ? '来源' : 'Source',
    prev: zh ? '前值' : 'Prev',
    forecast: zh ? '预期' : 'Forecast',
    actual: zh ? '实际' : 'Actual',
    impact: zh ? '重要度' : 'Impact',
  };

  const renderImportance = (imp: string | undefined) => {
    if (!imp) return null;
    const s = IMPORTANCE_STYLE[imp] || IMPORTANCE_STYLE.Low;
    const label = zh
      ? imp === 'High' ? '高' : imp === 'Medium' ? '中' : '低'
      : imp === 'High' ? 'High' : imp === 'Medium' ? 'Med' : 'Low';
    return (
      <span className={`inline-flex items-center gap-1.5 rounded border px-1.5 py-0.5 text-xs font-medium ${s.badge}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
        {label}
      </span>
    );
  };

  const statusBadge = (ev: EventItem) => {
    const past = isPast(ev.date);
    if (past) {
      return (
        <span className="rounded-full bg-gf-surface2 px-2 py-0.5 text-[11px] text-gf-muted">{t.ended}</span>
      );
    }
    const dd = dayDiff(ev.date);
    return (
      <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${dd <= 3 ? 'bg-gf-negative/15 text-gf-negative' : 'bg-gf-positive/15 text-gf-positive'}`}>
        {dd <= 0 ? t.today : `${dd}d`}
      </span>
    );
  };

  const renderUnlock = (ev: EventItem) => {
    const sym = (ev.symbol || '?').toUpperCase();
    const reminded = remindedKeys.has(ev.eventKey);
    return (
      <div className="card flex items-center gap-3 p-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gf-accent/15 text-sm font-bold text-gf-accent">
          {sym.slice(0, 4)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-semibold">{ev.title}</span>
            {statusBadge(ev)}
          </div>
          {ev.description && <div className="mt-0.5 truncate text-xs text-gf-muted">{ev.description}</div>}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
            <span className="text-gf-muted">
              {t.quantity} <span className="font-mono font-medium text-gf-text">{fmtTokens(ev.tokens)}</span>
            </span>
            {ev.pctSupply != null && (
              <span className="text-gf-muted">
                {t.pctSupply} <span className="font-mono font-medium text-gf-text">{ev.pctSupply.toFixed(2)}%</span>
              </span>
            )}
            {ev.valuation != null && (
              <span className="text-gf-muted">
                {t.valuation}{' '}
                <span className="font-mono font-medium text-gf-accent">{formatPrice(ev.valuation)}</span>
              </span>
            )}
          </div>
        </div>
        <button
          onClick={() => handleRemind(ev)}
          className={`shrink-0 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
            reminded
              ? 'border-gf-positive/40 bg-gf-positive/15 text-gf-positive'
              : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
          }`}
        >
          {reminded ? `✓ ${t.reminded}` : `🔔 ${t.remind}`}
        </button>
      </div>
    );
  };

  const renderEcon = (ev: EventItem) => {
    const past = isPast(ev.date);
    const reminded = remindedKeys.has(ev.eventKey);
    return (
      <div className={`card p-3 ${past ? 'opacity-60' : ''}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-semibold leading-tight">
              {zh ? translateEventTitle(ev.title) : ev.title}
            </div>
            <div className="mt-1 flex items-center gap-2 text-xs text-gf-muted">
              <span>{COUNTRY_FLAG[ev.country || ''] || '🌐'} {zh ? COUNTRY_ZH[ev.country || ''] || ev.country : ev.country}</span>
              {ev.dateType === 'dateOnly'
                ? <span className="font-mono text-xs">{fmtDate(ev.date)}</span>
                : <span className="font-mono">{fmtTime(ev.date)}</span>}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <button
              onClick={() => handleRemind(ev)}
              className={`rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                reminded
                  ? 'border-gf-positive/40 bg-gf-positive/15 text-gf-positive'
                  : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
              }`}
            >
              {reminded ? `✓ ${t.reminded}` : `🔔 ${t.remind}`}
            </button>
            {renderImportance(ev.importance)}
          </div>
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2 text-center text-xs">
          <div className="rounded-lg bg-gf-surface2 p-2">
            <div className="text-gf-muted">{t.prev}</div>
            <div className="mt-0.5 font-medium text-gf-text">{ev.previous || '—'}</div>
          </div>
          <div className="rounded-lg bg-gf-surface2 p-2">
            <div className="text-gf-muted">{t.forecast}</div>
            <div className="mt-0.5 font-medium text-gf-accent">{ev.forecast || '—'}</div>
          </div>
          <div className="rounded-lg bg-gf-surface2 p-2">
            <div className="text-gf-muted">{t.actual}</div>
            <div className="mt-0.5 font-medium text-gf-positive">{ev.actual || '—'}</div>
          </div>
        </div>
      </div>
    );
  };

  
  return (
    <div className="mx-auto max-w-[1200px] px-3 py-4 sm:px-4">
      <EventPing event="research_start" />
      <ResearchTabs />

      {/* Header + toolbar */}
      <div className="mb-4">
        <h1 className="text-xl font-bold sm:text-2xl">{t.title}</h1>
        <p className="mt-0.5 text-xs text-gf-muted">{t.subtitle}</p>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-gf-border bg-gf-surface2 p-0.5">
          <button
            onClick={() => setScope('all')}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${scope === 'all' ? 'bg-gf-accent text-white' : 'text-gf-muted hover:text-gf-text'}`}
          >
            {t.all}
          </button>
          <button
            onClick={() => {
              if (!authed) {
                router.push(`/login?redirect=${encodeURIComponent('/research/events')}`);
                return;
              }
              setScope('watchlist');
            }}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${scope === 'watchlist' ? 'bg-gf-accent text-white' : 'text-gf-muted hover:text-gf-text'}`}
          >
            {t.watchlist}
          </button>
        </div>
        <div className="flex rounded-lg border border-gf-border bg-gf-surface2 p-0.5">
          {(['all', 'unlock', 'economic'] as const).map((ty) => (
            <button
              key={ty}
              onClick={() => setType(ty)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${type === ty ? 'bg-gf-accent text-white' : 'text-gf-muted hover:text-gf-text'}`}
            >
              {ty === 'all' ? t.typeAll : ty === 'unlock' ? t.typeUnlock : t.typeEcon}
            </button>
          ))}
        </div>
        <div className="flex rounded-lg border border-gf-border bg-gf-surface2 p-0.5">
          {[30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${days === d ? 'bg-gf-accent text-white' : 'text-gf-muted hover:text-gf-text'}`}
            >
              {d === 30 ? t.day30 : t.day90}
            </button>
          ))}
        </div>
        {summary && (
          <span className="text-xs text-gf-muted">
            {summary.unlock} {t.unlock} · {summary.economic} {t.economic}
          </span>
        )}
      </div>

      {/* Scope guard */}
      {scope === 'watchlist' && !authed ? (
        <div className="card flex flex-col items-center gap-3 p-10 text-center">
          <div className="text-3xl">🔐</div>
          <div className="text-sm text-gf-muted">{t.needLogin}</div>
          <button
            onClick={() => router.push(`/login?redirect=${encodeURIComponent('/research/events')}`)}
            className="rounded-lg bg-gf-accent px-4 py-2 text-sm font-medium text-white"
          >
            {t.login}
          </button>
        </div>
      ) : eventsQuery.isLoading ? (
        <div className="card p-10 text-center text-gf-muted">{zh ? '加载中...' : 'Loading...'}</div>
      ) : groups.length === 0 ? (
        <div className="card p-10 text-center text-gf-muted">{t.empty}</div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left 2/3: date-grouped events */}
          <div className="lg:col-span-2">
            {groups.map((g) => (
              <div key={g.date} className="mb-5">
                <div className="mb-2 flex items-center gap-2">
                  <span className={`text-sm font-semibold ${isToday(g.date) ? 'text-gf-accent' : 'text-gf-text'}`}>
                    {isToday(g.date) ? `📌 ${t.today} · ${fmtDate(g.date)}` : fmtDate(g.date)}
                  </span>
                  <span className="h-px flex-1 bg-gf-border" />
                  <span className="text-xs text-gf-muted">{g.events.length}</span>
                </div>
                <div className="space-y-2">
                  {g.events.map((ev) => (ev.type === 'unlock' ? renderUnlock(ev) : renderEcon(ev)))}
                </div>
              </div>
            ))}
          </div>

          {/* Right 1/3: watchlist summary + supply notes */}
          <div className="space-y-4">
            <div className="card p-4">
              <div className="mb-2 text-sm font-semibold">⭐ {t.watchSummary}</div>
              {!authed ? (
                <button
                  onClick={() => router.push(`/login?redirect=${encodeURIComponent('/research/events')}`)}
                  className="w-full rounded-lg border border-gf-border bg-gf-surface2 py-2 text-xs text-gf-muted hover:text-gf-text"
                >
                  {t.login} →
                </button>
              ) : watchEvents.length === 0 ? (
                <div className="py-4 text-center text-xs text-gf-muted">
                  {t.watchEmpty}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {watchEvents.map((ev) => (
                    <button
                      key={ev.eventKey}
                      onClick={() => handleRemind(ev)}
                      className="flex w-full flex-col items-stretch gap-0.5 rounded-lg bg-gf-surface2 px-3 py-2 text-left text-xs hover:bg-gf-border/40"
                    >
                      <span className="flex w-full items-center justify-between gap-2">
                        <span className="min-w-0 truncate font-medium text-gf-text">{ev.title}</span>
                        <span className="shrink-0 font-mono text-gf-muted">{fmtDate(ev.date)}</span>
                      </span>
                      {/* D17: event time revised — show the change instead of silently using the old time */}
                      {ev.timeRevised && (
                        <span className="flex w-full items-center gap-1 rounded bg-gf-warn/10 px-1.5 py-0.5 text-[10px] text-gf-warn">
                          ⏱ {zh ? '时间已修订' : 'Time revised'}
                          {ev.prevDate && (
                            <span className="text-gf-muted line-through">{fmtDate(ev.prevDate)}</span>
                          )}
                          <span>→ {fmtDate(ev.date)}</span>
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="card p-4">
              <div className="mb-1.5 text-sm font-semibold">📘 {t.supplyNote}</div>
              <p className="text-xs leading-relaxed text-gf-muted">{t.supplyNoteBody}</p>
            </div>
          </div>
        </div>
      )}

      {/* Reminder dialog */}
      {remindFor && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 sm:items-center">
          <div className="w-full max-w-sm rounded-2xl border border-gf-border bg-gf-bg p-5">
            <div className="mb-1 text-base font-semibold">🔔 {t.remindTitle}</div>
            <div className="mb-1 text-xs text-gf-muted">
              {remindFor.title} · {fmtDate(remindFor.date)} {remindFor.dateType === 'dateOnly' ? '' : fmtTime(remindFor.date)}
            </div>
            {/* D17: timezone summary */}
            <div className="mb-3 rounded bg-gf-surface2 px-2 py-1 text-[10px] text-gf-muted">
              {t.tzSummary}: {tzLabel}
            </div>
            {/* D17: TBD-time events may only be reminded by day */}
            {remindFor.dateType === 'dateOnly' && (
              <div className="mb-3 rounded border border-gf-warn/30 bg-gf-warn/10 px-3 py-2 text-[11px] text-gf-warn">
                ⚠️ {t.dayOnlyHint}
              </div>
            )}
            <div className="mb-2 text-xs font-medium text-gf-text">{t.channelTitle}</div>
            <div className="mb-3 grid grid-cols-2 gap-2">
              {(['inapp', 'both'] as const).map((ch) => (
                <button
                  key={ch}
                  onClick={() => setRemindChannel(ch)}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    remindChannel === ch
                      ? 'border-gf-accent bg-gf-accent/15 text-gf-accent'
                      : 'border-gf-border bg-gf-surface2 text-gf-muted hover:text-gf-text'
                  }`}
                >
                  {ch === 'inapp' ? t.channelInapp : t.channelBoth}
                </button>
              ))}
            </div>
            <div className="mb-3 rounded-lg bg-gf-surface2 px-3 py-2 text-[11px] leading-relaxed text-gf-muted">
              {t.channelNote}
            </div>
            <div className="mb-3 text-xs text-gf-muted">{t.remindSub}</div>
            <div className="space-y-2">
              {[1, 24, 72].map((h) => {
                const dayOnly = remindFor.dateType === 'dateOnly' && h !== 24;
                return (
                  <button
                    key={h}
                    disabled={saving || dayOnly}
                    onClick={() => submitReminder(h)}
                    className={`w-full rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors disabled:opacity-40 ${
                      dayOnly
                        ? 'cursor-not-allowed border-gf-border bg-gf-surface2 text-gf-muted'
                        : 'border-gf-border bg-gf-surface2 text-gf-text hover:border-gf-accent hover:text-gf-accent'
                    }`}
                  >
                    {h === 1 ? t.lead1h : h === 24 ? t.lead24h : t.lead3d}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setRemindFor(null)}
              className="mt-3 w-full rounded-lg px-3 py-2 text-sm text-gf-muted hover:text-gf-text"
            >
              {t.cancel}
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-20 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-gf-accent px-4 py-2 text-sm font-medium text-white shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
